import { PageHeader, PageState } from "@/components/ui/page";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
	FileCheck,
	Gift,
	CreditCard,
	Bot,
	Box,
	Plus,
	LucideIcon,
	User,
	Award,
	Percent,
	X,
	Clock,
	Search,
} from "lucide-react";
import { addonsAPI, addonRequestsAPI, getApiErrorPayload, Addon, AddonRequest, AddonRequestStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { usePermission } from "@/hooks/usePermission";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { cn, formatDate } from "@/lib/utils";
import { confirmAction } from "@/components/ui/confirm-dialog";

const ICON_MAP: Record<string, LucideIcon> = {
	fileCheck: FileCheck,
	gift: Gift,
	creditCard: CreditCard,
	user: User,
	award: Award,
	discount: Percent,
	percent: Percent,
	bot: Bot,
};

function getAddonIcon(iconKey: string): LucideIcon {
	return ICON_MAP[iconKey] ?? Box;
}

function requesterName(req: AddonRequest): string {
	return [req.createdBy?.firstName, req.createdBy?.lastName].filter(Boolean).join(" ");
}

function statusLabel(status: AddonRequestStatus, t: (key: string) => string): string {
	return t(`addonRequests.status.${status}`);
}

const GROUPS = [
	{
		key: "sales",
		codes: ["discount_coupons", "customer_balances", "loyalty_points", "gift_cards", "loyalty_apps"],
		tone: "bg-violet-soft text-violet",
	},
	{ key: "payments", codes: ["zatca", "bnpl", "tips"], tone: "bg-success-soft text-success" },
	{ key: "tools", codes: ["ai_assistant"], tone: "bg-info-soft text-info" },
	{ key: "other", codes: [] as string[], tone: "bg-orange-soft text-orange" },
];
function groupFor(addon: Addon) {
	return GROUPS.find(group => group.codes.includes(addon.code || "")) || GROUPS[3];
}
export default function IntegrationsPage() {
	const { t, i18n } = useTranslation();
	const { toast } = useToast();
	const canCreate = usePermission("settings.create");
	const canDelete = usePermission("settings.delete");
	const [search, setSearch] = useState("");
	const [filter, setFilter] = useState("all");

	// Request add-on dialog
	const [modalOpen, setModalOpen] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [formName, setFormName] = useState("");
	const [formDescription, setFormDescription] = useState("");

	const {
		data: addons = [],
		isLoading: addonsLoading,
		isError: addonsError,
		refetch: refetchAddons,
	} = useQuery({
		queryKey: ["addons"],
		queryFn: () => addonsAPI.getAll(),
	});

	const {
		data: requests = [],
		isLoading: requestsLoading,
		isError: requestsError,
		refetch: refetchRequests,
	} = useQuery({
		queryKey: ["addon-requests"],
		queryFn: () => addonRequestsAPI.getAll(),
	});

	const handleSubmitRequest = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!canCreate || !formName.trim() || !formDescription.trim()) return;
		setSubmitLoading(true);
		try {
			await addonRequestsAPI.create({ name: formName.trim(), description: formDescription.trim() });
			toast({ title: t("common.success"), description: t("addons.messages.requestSubmitted") });
			setModalOpen(false);
			setFormName("");
			setFormDescription("");
			void refetchRequests();
		} catch (err) {
			const payload = getApiErrorPayload(err);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		} finally {
			setSubmitLoading(false);
		}
	};

	const handleDeleteRequest = async (id: number) => {
		if (!canDelete) return;
		if (!(await confirmAction(t("addonRequests.confirmations.delete"), { confirmLabel: t("common.delete") })))
			return;
		try {
			await addonRequestsAPI.delete(id);
			toast({ title: t("common.success"), description: t("addonRequests.messages.deleted") });
			void refetchRequests();
		} catch {
			toast({
				title: t("common.error"),
				description: t("addonRequests.errors.deleteFailed"),
				variant: "destructive",
			});
		}
	};

	const query = search.trim().toLocaleLowerCase();
	const catalog = addons.map(addon =>
		i18n.language.startsWith("ar")
			? addon
			: { ...addon, name: addon.nameEn || addon.name, description: addon.descriptionEn || addon.description },
	);
	const visible = catalog.filter(
		addon =>
			(filter === "all" || (filter === "active" ? addon.status === "active" : addon.status !== "active")) &&
			`${addon.name} ${addon.description} ${t(`integrations.groups.${groupFor(addon).key}.title`)}`
				.toLocaleLowerCase()
				.includes(query),
	);
	const resetFilters = () => {
		setSearch("");
		setFilter("all");
	};
	return (
		<div className="space-y-7 p-4 md:p-6" data-testid="integrations-page">
			<PageHeader
				title={t("navigation.integrations")}
				description={t("integrations.description")}
				actions={
					canCreate ? (
						<Button onClick={() => setModalOpen(true)}>
							<Plus className="h-4 w-4" aria-hidden />
							{t("addons.requestAddon")}
						</Button>
					) : undefined
				}
			/>
			{!canCreate && <p className="text-sm text-muted-foreground">{t("integrations.readOnly")}</p>}
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative w-full sm:max-w-sm">
					<Search
						className="pointer-events-none absolute start-3 top-3.5 h-4 w-4 text-muted-foreground"
						aria-hidden
					/>
					<Input
						type="search"
						aria-label={t("integrations.search")}
						placeholder={t("integrations.search")}
						value={search}
						onChange={e => setSearch(e.target.value)}
						className="ps-10"
					/>
				</div>
				<div className="flex flex-wrap gap-1" role="group" aria-label={t("integrations.filter")}>
					{["all", "active", "planned"].map(value => (
						<Button
							key={value}
							variant={filter === value ? "secondary" : "ghost"}
							aria-pressed={filter === value}
							onClick={() => setFilter(value)}
						>
							{t(`integrations.filters.${value}`)}
						</Button>
					))}
				</div>
				<span className="text-sm text-muted-foreground sm:ms-auto" role="status">
					{!addonsLoading && !addonsError && t("integrations.count", { count: visible.length })}
				</span>
			</div>
			<div data-testid="integration-catalog" className="space-y-7">
				{addonsLoading ? (
					<Loading />
				) : addonsError ? (
					<PageState
						error
						title={t("integrations.catalogError")}
						action={
							<Button variant="outline" onClick={() => void refetchAddons()}>
								{t("integrations.retry")}
							</Button>
						}
					/>
				) : !addons.length ? (
					<PageState title={t("integrations.empty")} description={t("integrations.emptyDescription")} />
				) : !visible.length ? (
					<PageState
						title={t("integrations.noMatches")}
						action={
							<Button variant="outline" onClick={resetFilters}>
								{t("integrations.clearFilters")}
							</Button>
						}
					/>
				) : (
					GROUPS.map(group => {
						const items = visible.filter(addon => groupFor(addon).key === group.key);
						if (!items.length) return null;
						return (
							<section key={group.key} aria-labelledby={`integration-group-${group.key}`}>
								<div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
									<h2 id={`integration-group-${group.key}`} className="text-base font-semibold">
										{t(`integrations.groups.${group.key}.title`)}
									</h2>
									<p className="text-sm text-muted-foreground">
										{t(`integrations.groups.${group.key}.description`)}
									</p>
								</div>
								<div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
									{items.map(addon => (
										<AddonCard key={addon.id} addon={addon} tone={group.tone} />
									))}
								</div>
							</section>
						);
					})
				)}
			</div>
			<section aria-labelledby="integration-requests-title" className="border-t pt-6">
				<div className="mb-4 space-y-1">
					<h2 id="integration-requests-title" className="text-base font-semibold">
						{t("integrations.requests")}
					</h2>
					<p className="text-sm text-muted-foreground">{t("integrations.requestsDescription")}</p>
				</div>
				{requestsLoading ? (
					<Loading />
				) : requestsError ? (
					<PageState
						error
						title={t("integrations.requestsError")}
						action={
							<Button variant="outline" onClick={() => void refetchRequests()}>
								{t("integrations.retry")}
							</Button>
						}
					/>
				) : requests.length ? (
					<div className="space-y-3">
						{requests.map(request => (
							<RequestCard
								key={request.id}
								request={request}
								canDelete={canDelete}
								onDelete={() => handleDeleteRequest(request.id)}
							/>
						))}
					</div>
				) : (
					<p className="py-4 text-sm text-muted-foreground">{t("integrations.noRequests")}</p>
				)}
			</section>

			{/* Request add-on dialog */}
			<Dialog open={modalOpen} onOpenChange={setModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("addons.modal.title")}</DialogTitle>
						<DialogDescription>{t("addons.requestAddonDescription")}</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleSubmitRequest} className="space-y-4">
						<div>
							<Label htmlFor="addon-name">{t("addons.modal.addonName")}</Label>
							<Input
								id="addon-name"
								value={formName}
								onChange={e => setFormName(e.target.value)}
								placeholder={t("addons.modal.addonNamePlaceholder")}
								className="mt-2"
								required
							/>
						</div>
						<div>
							<Label htmlFor="addon-desc">{t("addons.modal.description")}</Label>
							<Textarea
								id="addon-desc"
								value={formDescription}
								onChange={e => setFormDescription(e.target.value)}
								placeholder={t("addons.modal.descriptionPlaceholder")}
								className="mt-2 min-h-[100px]"
								required
							/>
						</div>
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
								{t("common.cancel")}
							</Button>
							<Button type="submit" disabled={submitLoading}>
								{submitLoading ? t("common.loading") : t("addons.requestAddon")}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function AddonCard({ addon, tone }: { addon: Addon; tone: string }) {
	const { t } = useTranslation();
	const Icon = getAddonIcon(addon.icon);
	return (
		<Card className="min-w-0 p-4 shadow-none" data-testid="integration-card">
			<div className="mb-3 flex items-start justify-between gap-3">
				<span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl", tone)}>
					<Icon className="h-5 w-5" aria-hidden />
				</span>
				<Badge variant={addon.status === "active" ? "success" : "warning"}>
					{t(addon.status === "active" ? "integrations.filters.active" : "integrations.filters.planned")}
				</Badge>
			</div>
			<h3 dir="auto" className="mb-1 break-words text-base font-semibold">
				{addon.name}
			</h3>
			<p dir="auto" className="break-words text-sm leading-relaxed text-muted-foreground">
				{addon.description}
			</p>
			{addon.badge && !["قريباً", "coming_soon"].includes(addon.badge) && (
				<p className="mt-3 text-xs text-muted-foreground">
					{addon.badge === "featured" ? t("addons.featured") : addon.badge}
				</p>
			)}
		</Card>
	);
}

function RequestCard({
	request,
	canDelete,
	onDelete,
}: {
	request: AddonRequest;
	canDelete: boolean;
	onDelete: () => void;
}) {
	const { t } = useTranslation();
	const status = request.status ?? "pending";
	const name = requesterName(request);
	return (
		<Card className="flex flex-wrap items-start gap-3 p-4 shadow-none">
			<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
				<Clock className="h-5 w-5" aria-hidden />
			</span>
			<div className="min-w-0 flex-1 basis-56 space-y-2">
				<div className="flex flex-wrap items-center gap-2">
					<h3 dir="auto" className="break-words font-semibold">
						{request.name}
					</h3>
					<Badge variant={status === "completed" ? "success" : status === "in_progress" ? "info" : "warning"}>
						{statusLabel(status, t)}
					</Badge>
				</div>
				<p dir="auto" className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
					{request.description}
				</p>
				{request.notes && (
					<p dir="auto" className="whitespace-pre-wrap break-words border-s-2 ps-3 text-sm">{request.notes}</p>
				)}
				<p className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
					{name && (
						<span>
							{t("addonRequests.table.requestedBy")}: {name}
						</span>
					)}
					<time dateTime={request.createdAt}>{formatDate(request.createdAt)}</time>
				</p>
			</div>
			{canDelete && (
				<Button
					variant="ghost"
					onClick={onDelete}
					aria-label={`${t("addonRequests.actions.cancelRequest")}: ${request.name}`}
				>
					<X className="h-4 w-4" aria-hidden />
					{t("addonRequests.actions.cancelRequest")}
				</Button>
			)}
		</Card>
	);
}
